import { api } from '../core';
import { CASES_FILTERS_TAG } from '../constants';
import { RootState, setCasesFilters, setSelectedCasesFilters } from '@/store';

//** BEGIN StatusOptions */
export const CasesFiltersStatusOptions = {
  NEW: 'New',
  DOCUMENT_UPLOAD: 'Document Upload',
  CLOSED: 'Closed',
  AWAITING_CMS_CLOSURE: 'Awaiting CMS Closure',
  READY_FOR_AI_PROCESSING: 'Ready for AI Processing',
  WAITING_FOR_DOCUMENT_PROCESSING: 'Waiting For Document Processing',
} as const;

export type CaseStatusOptionsKeys = (typeof CasesFiltersStatusOptions)[keyof typeof CasesFiltersStatusOptions];

// Statuses that are hidden by default on page load — the user can still opt
// in via the Status column filter. Kept as a Set of rendered labels so the
// filter check is a single lookup rather than an OR chain, and so adding
// another hidden-by-default status later is a one-line change.
export const DEFAULT_HIDDEN_STATUS_LABELS: ReadonlySet<string> = new Set([
  CasesFiltersStatusOptions.CLOSED,
  CasesFiltersStatusOptions.AWAITING_CMS_CLOSURE,
]);
//** END StatusOptions */

//** BEGIN UrgentOptions */
export const CasesFiltersUrgentOptions = {
  YES: 'Yes',
  NO: 'No',
} as const;

export type CasesFiltersUrgentOptionsKeys = (typeof CasesFiltersUrgentOptions)[keyof typeof CasesFiltersUrgentOptions];
//** END UrgentOptions */

export interface CasesFiltersUser {
  userId: string;
  userName: string;
}

export interface CaseStatusOption {
  raw: CaseStatusOptionsKeys;
  rendered: string;
}

export interface CasesFiltersResponse {
  assignedTo: (CasesFiltersUser | null)[];
  createdBy: CasesFiltersUser[];
  status: CaseStatusOption[];
}

type CasesFiltersProps = object;

const casesFiltersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCasesFilters: builder.query<CasesFiltersResponse, CasesFiltersProps>({
      query: () => {
        return {
          url: `cases/filters`,
          method: 'GET',
        };
      },
      providesTags: () => [{ type: CASES_FILTERS_TAG }],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as RootState;

          if (data && data.createdBy && data.assignedTo && data.status) {
            dispatch(
              setCasesFilters({
                ...data,
                isUrgent: Object.values(CasesFiltersUrgentOptions),
              }),
            );

            dispatch(
              setSelectedCasesFilters({
                ...state.casesFilters.selected,
                status: data.status.filter((status) => !DEFAULT_HIDDEN_STATUS_LABELS.has(status.rendered)),
              }),
            );
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getCasesFilters in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetCasesFiltersQuery } = casesFiltersApi;
